/* eslint-disable max-len */
import {
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  useTranslate,
  useUpdate,
  useNotify,
  useRefresh
} from "react-admin";
import * as User from "../providers/user";
import { useState } from "react";
import { FIND_FIRST_USER_BY_PHONE, GET_LOCATIONS } from "../gql";
import { useLazyQuery, useQuery } from "@apollo/client";
import { CustomButton } from "./CustomButton";
import CheckUserByPhoneNumber from "./CheckUserByPhoneNumber";
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone";
import { FailedOrderIcon } from "assets/Icons";
import { getRawPhoneNumber } from "utils/getRawPhoneNumber";
import BookFromArtistCalendarFormHelper from "./BookFromArtistCalendarFormHelper";

interface AddUserToOrderModalProps {
  open: boolean;
  onClose: () => void;
  addUserToOrder: {
    id: string | undefined;
    charge: boolean;
  };
  openChargeModal: () => void;
}

interface LocationResult {
  id: string;
  company_id: string;
  timezone: string;
}

export default function AddUserToOrderModal({
  open,
  onClose,
  addUserToOrder,
  openChargeModal
}: AddUserToOrderModalProps) {
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const [edit] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [errors, setErrors] = useState<any>({});
  const [resetUserInputs, setResetUserInputs] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isCustomerSearchTriggered, setIsCustomerSearchTriggered] =
    useState(false);
  const [userSelectMode, setUserSelectMode] = useState<string | null>(
    "customers"
  );
  const [isCloseIconShaking, setIsCloseIconShaking] = useState(false);
  const isCustomErrors = Object.keys(errors)?.length > 0;
  const [findUserByPhone, { data: findUserData }] = useLazyQuery(
    FIND_FIRST_USER_BY_PHONE
  );

  const locationsResult = useQuery(GET_LOCATIONS, {
    variables: {
      where: {
        company_id: { equals: permissions?.companyId }
      }
    },
    fetchPolicy: "no-cache"
  });
  const locationsData: LocationResult[] =
    (locationsResult?.data ?? locationsResult?.previousData)?.locations ?? [];

  const createValidationSchema = () => {
    const userSchema = {
      search: {
        // phone_number_search: yup
        //   .string()
        //   .test("phone", "Invalid phone number", (value) => {
        //     return isPhoneValid(value);
        //   })
      },
      customers: {
        user_id: yup
          .string()
          .nullable()
          .required("Customer selection required.")
      }
    };
    return yup
      .object()
      .shape({
        ...userSchema[userSelectMode as keyof typeof userSchema]
      })
      .required();
  };

  const submit = async (submitData: any) => {
    let customErrors: any = {};
    if (userSelectMode === "search" && !findUserData?.findFirstUser?.id) {
      customErrors.foundUser = translate(
        "appointment.no_existing_customer_found"
      );
    }
    if (Object.keys(customErrors)?.length > 0) {
      setErrors(customErrors);
      return;
    }
    try {
      setIsSaving(true);
      const { user_id } = submitData;

      const orderPayload = {
        ...(userSelectMode === "customers" && {
          user: {
            id: user_id
          }
        }),
        ...(userSelectMode === "search" && {
          user: {
            id: findUserData?.findFirstUser?.id
          }
        })
      };

      edit(
        "Order",
        {
          id: addUserToOrder?.id,
          data: orderPayload
        },
        {
          onSuccess: () => {
            // notify("Order updated successfully", { type: "success" });
            refresh();
            if (addUserToOrder?.charge) {
              setTimeout(() => {
                openChargeModal();
              }, 500);
            }
            onClose();
          },
          onSettled: () => setIsSaving(false),
          onError: (error: any) => {
            notify("Order update failed", { type: "warning" });
            setIsSaving(false);
          }
        }
      );

      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      setErrors({ networkError: error?.response?.data?.message });
    }
  };

  const disabled =
    permissions.role.role !== "OWNER" &&
    permissions.role.role !== "MANAGER" &&
    !permissions.isSuperAdmin;

  const checkUser = (phoneNumber: string) => {
    findUserByPhone({
      variables: {
        where: {
          OR: [
            {
              phone_number: {
                equals: phoneNumber
              }
            },
            {
              libphonenumber: {
                equals: getRawPhoneNumber(phoneNumber)
              }
            }
          ]
        }
      },
      fetchPolicy: "no-cache"
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason: any) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      sx={{
        "& .MuiDialogContent-root": {
          padding: "0px"
        },
        "& .MuiDialog-paper": {
          borderRadius: "8px",
          backgroundColor: "#fff",
          maxWidth: "480px",
          width: "100%",
          "@media screen and (max-width:926px)": {
            maxWidth: "480px",
            marginLeft: "4px",
            marginRight: "4px"
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          padding: "4px 16px 0px 16px"
        }}
      >
        <div className="w-full">
          <div className="w-full flex justify-between">
            <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600] uppercase">
              {translate("appointment.select_customer")}
            </div>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            backgroundColor: "#fff",
            "& .RaToolbar-desktopToolbar": {
              backgroundColor: "#fff",
              minHeight: "40px",
              padding: "0px 16px 16px 16px"
            },
            "& .RaToolbar-mobileToolbar": {
              position: "relative !important",
              backgroundColor: "#fff",
              minHeight: "40px",
              padding: "0px 16px 16px 16px !important"
              // zIndex: 90
            },
            "& .RaCreate-noActions": {
              marginTop: 0
            }
          }}
        >
          <SimpleForm
            onSubmit={submit}
            // validate={validateDates}
            resolver={yupResolver(createValidationSchema())}
            className="create-form"
            id="save-create-appointment"
            sx={{
              // backgroundColor: "#fff",
              padding: "0px 16px !important",
              "& .MuiFilledInput-root": {
                borderRadius: "8px",
                border: "1px solid rgba(48, 47, 46, 0.06)"
              },
              "& .MuiFilledInput-input": {
                height: "24px",
                fontSize: "16px",
                borderRadius: "8px",
                fontFamily: "Inter",
                border: "none"
              },
              "& .MuiFilledInput-input:focus": {
                boxShadow: "none",
                borderRadius: "8px !important"
              },
              "& .MuiFilledInput-root:before": {
                border: "none !important"
              },
              "& .MuiFilledInput-root:after": {
                border: "1px solid #b8722a !important",
                top: "0px",
                borderRadius: "8px",
                transition: "none"
              },
              "& .MuiFilledInput-root.Mui-error:after": {
                border: "1px solid #EF5B1B !important"
              },
              "& .MuiFilledInput-root.Mui-disabled": {
                backgroundColor: "rgba(48, 47, 46, 0.06)"
              },
              "& .MuiTextField-root": {
                marginTop: "3px",
                marginBottom: "3px"
              },
              "& .MuiInputLabel-root": {
                fontSize: "16px",
                color: "rgba(48, 47, 46, 0.6)"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#b8722a"
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                marginTop: "2px",
                marginBottom: "2px",
                lineHeight: "14px"
              }
            }}
            toolbar={false}
          >
            <BookFromArtistCalendarFormHelper
              resetUserInputs={resetUserInputs}
            />
            <div className="w-full">
              <div className="w-full flex gap-2 justify-between my-5">
                <CustomButton
                  variant={
                    userSelectMode === "customers" ? "primary" : "secondary"
                  }
                  // disabled={userSelectMode === "customers" ? true : false}
                  type="button"
                  text={translate("appointment.customers")}
                  onClick={() => {
                    if (
                      findUserData?.findFirstUser?.id &&
                      userSelectMode === "search"
                    ) {
                      setIsCloseIconShaking(true);
                      setTimeout(() => {
                        setIsCloseIconShaking(false);
                      }, 500);
                      return;
                    }
                    setUserSelectMode("customers");
                    setResetUserInputs((prev) => !prev);
                  }}
                  sx={{ width: "95%" }}
                />
                <CustomButton
                  variant={
                    userSelectMode === "search" ? "primary" : "secondary"
                  }
                  // disabled={userSelectMode === "search" ? true : false}
                  type="button"
                  text={translate("appointment.search_by_phone")}
                  onClick={() => {
                    setUserSelectMode("search");
                    if (!findUserData?.findFirstUser?.id) {
                      setResetUserInputs((prev) => !prev);
                    }
                  }}
                  sx={{ width: "110%" }}
                />
              </div>
              {userSelectMode === "customers" && (
                <ReferenceInput
                  source="user_id"
                  reference="User"
                  filter={{
                    OR: [
                      {
                        booked_appointments: {
                          some: {
                            location_id: {
                              in: locationsData?.map((location) => location.id)
                            }
                          }
                        }
                      },
                      {
                        joined_company_id: permissions.companyId
                      }
                    ]
                  }}
                  enableGetChoices={({ q }) => {
                    return q?.trim()?.length > 2;
                  }}
                  perPage={10}
                >
                  <AutocompleteInput
                    optionText={(value) =>
                      `${value?.first_name} ${value?.last_name}`
                    }
                    shouldRenderSuggestions={(val: any) => {
                      return val?.trim()?.length > 2;
                    }}
                    label={translate("appointment.customers")}
                    fullWidth
                    noOptionsText={translate(
                      "appointment.search_to_find_customers"
                    )}
                    isRequired
                  />
                </ReferenceInput>
              )}
              {userSelectMode === "search" && (
                <div className="pt-[3px] mb-[5px]">
                  <CheckUserByPhoneNumber
                    checkFunction={checkUser}
                    userId={findUserData?.findFirstUser?.id}
                    isCustomerSearchTriggered={isCustomerSearchTriggered}
                    setIsCustomerSearchTriggered={setIsCustomerSearchTriggered}
                  />
                </div>
              )}
              {findUserData?.findFirstUser?.id &&
                userSelectMode === "search" && (
                  <div
                    aria-hidden
                    className="flex w-min items-center gap-[8px] rounded-[20px] mb-3 transition-colors duration-200 ease-in-out"
                    style={{
                      backgroundColor: "rgba(184, 114, 42, 0.1)"
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "rgba(238, 234, 228, 0.9)",
                        color: "rgba(48, 47, 46, 0.95)",
                        fontFamily: "Inter",
                        fontSize: "18px"
                      }}
                      alt={findUserData?.findFirstUser?.first_name || "avatar"}
                      src={findUserData?.findFirstUser?.photo_url || "monogram"}
                    />
                    <div
                      className="max-w-[150px] truncate whitespace-nowrap text-[16px] font-[400] font-['Inter']"
                      style={{
                        color: "#B8722A"
                      }}
                    >
                      {`${findUserData?.findFirstUser?.first_name} ${findUserData?.findFirstUser?.last_name}`}
                    </div>
                    <IconButton
                      style={{ width: "40px", height: "40px" }}
                      className={isCloseIconShaking ? "shakeAnimation" : ""}
                      onClick={() => {
                        checkUser("");
                        setIsCustomerSearchTriggered(false);
                      }}
                    >
                      <CloseIcon
                        style={{
                          color: "#EF5B1B",
                          width: "22px",
                          height: "22px"
                        }}
                      />
                    </IconButton>
                  </div>
                )}
            </div>
          </SimpleForm>
          {isCustomErrors && (
            <Dialog
              open={isCustomErrors}
              onClose={() => setErrors({})}
              sx={{
                "& .MuiDialogContent-root": {
                  padding: "0px"
                },
                "& .MuiDialog-paper": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  maxWidth: "520px",
                  width: "100%"
                }
              }}
            >
              <DialogTitle
                sx={{ display: "flex", padding: "4px 16px 0px 16px" }}
              >
                <div className="w-full flex justify-center items-center ml-[-12px] text-[14px] font-['Inter'] font-[600]" />
                <IconButton onClick={() => setErrors({})}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <div className="w-full flex flex-col justify-center items-center px-2">
                  <FailedOrderIcon />

                  <p className="text-[24px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-4">
                    {errors?.networkError
                      ? "Something went wrong"
                      : translate(
                          "appointment.you_need_to_fill_required_fields"
                        )}
                  </p>
                  <div className="my-6">
                    {Object.values(errors).map((error: any, index: number) => (
                      <p
                        key={index}
                        className="text-[18px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-1"
                      >
                        {errors?.networkError ? "" : index + 1 + "."} {error}
                      </p>
                    ))}
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <div className="w-full flex justify-center mb-4">
                  <CustomButton
                    variant="primary"
                    text={translate("appointment.got_it")}
                    onClick={() => setErrors({})}
                  />
                </div>
              </DialogActions>
            </Dialog>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ paddingX: "16px" }}>
        <div className="w-full flex justify-between items-center">
          <div />
          <CustomButton
            type="submit"
            text={translate("common.save")}
            form="save-create-appointment"
            submitting={isSaving}
            disabled={isSaving || disabled}
          />
        </div>
      </DialogActions>
    </Dialog>
  );
}
